import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  Package, ShoppingBag, DollarSign, ChevronLeft, ChevronRight, 
  Search, Check, X, Loader2, Eye, Edit, Trash2, Plus, Save, Upload,
  FileText, Image, Users, Settings, Truck, ExternalLink, FileDown, ChevronDown, ChevronUp, Download
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import AdminSettingsPage from "@/components/AdminSettings";
import { API } from "@/lib/api";

// ===================
// DASHBOARD
// ===================
export function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setStats(res.data)).catch(console.error);
    }
  }, [token]);

  return (
    <AdminLayout active="dashboard">
      <h1 className="text-2xl font-bold text-slate-800 mb-6" data-testid="admin-dashboard">Dashboard</h1>
      {stats ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/produkty')}>
            <div className="flex items-center gap-3"><Package className="w-8 h-8 text-[#004E98]" /><div><p className="text-sm text-slate-500">Produktů</p><p className="text-2xl font-bold text-slate-800">{stats.products?.toLocaleString()}</p></div></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/objednavky')}>
            <div className="flex items-center gap-3"><ShoppingBag className="w-8 h-8 text-[#FF6B35]" /><div><p className="text-sm text-slate-500">Objednávek</p><p className="text-2xl font-bold text-slate-800">{stats.orders}</p></div></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-3"><DollarSign className="w-8 h-8 text-emerald-500" /><div><p className="text-sm text-slate-500">Tržby</p><p className="text-2xl font-bold text-slate-800">{stats.revenue?.toFixed(0)} Kč</p></div></div>
          </div>
        </div>
      ) : <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>}
    </AdminLayout>
  );
}

// ===================
// ORDERS
// ===================

const CARRIER_OPTIONS = [
  { value: 'ppl', label: 'PPL' },
  { value: 'zasilkovna', label: 'Zásilkovna' },
  { value: 'ceska_posta', label: 'Česká pošta' },
  { value: 'dpd', label: 'DPD' },
  { value: 'gls', label: 'GLS' },
  { value: 'other', label: 'Jiný' },
];

const STATUS_LABELS = {
  pending: { label: 'Čeká', color: 'bg-amber-100 text-amber-700' },
  confirmed: { label: 'Potvrzeno', color: 'bg-blue-100 text-blue-700' },
  processing: { label: 'Zpracování', color: 'bg-purple-100 text-purple-700' },
  shipped: { label: 'Odesláno', color: 'bg-cyan-100 text-cyan-700' },
  delivered: { label: 'Doručeno', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Zrušeno', color: 'bg-red-100 text-red-700' }
};

export function AdminOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [trackingForm, setTrackingForm] = useState({ carrier: 'ppl', tracking_number: '' });
  const [savingTracking, setSavingTracking] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const loadOrders = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : '';
    axios.get(`${API}/admin/orders${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setOrders(res.data); setSelected(new Set()); })
      .catch(() => toast.error('Chyba načtení objednávek'))
      .finally(() => setLoading(false));
  }, [token, statusFilter]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status?status=${newStatus}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Stav aktualizován');
      loadOrders();
    } catch { toast.error('Chyba aktualizace'); }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Opravdu smazat objednávku? Tato akce je nevratná.')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Objednávka smazána');
      loadOrders();
    } catch { toast.error('Chyba mazání'); }
  };

  const bulkDeleteOrders = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Opravdu smazat ${selected.size} objednávek?`)) return;
    try {
      await axios.post(`${API}/admin/orders/bulk-delete`, { ids: Array.from(selected) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Objednávky smazány');
      loadOrders();
    } catch { toast.error('Chyba mazání'); }
  };

  const toggleTracking = (order) => {
    if (expandedOrder === order.id) { setExpandedOrder(null); }
    else { setExpandedOrder(order.id); setTrackingForm({ carrier: order.carrier || 'ppl', tracking_number: order.tracking_number || '' }); }
  };

  const saveTracking = async (orderId) => {
    if (!trackingForm.tracking_number.trim()) { toast.error('Zadejte sledovací číslo'); return; }
    setSavingTracking(true);
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/tracking`, trackingForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sledovací číslo uloženo');
      loadOrders();
      setExpandedOrder(null);
    } catch { toast.error('Chyba ukládání'); }
    finally { setSavingTracking(false); }
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const downloadInvoice = (orderId, orderNumber) => {
    window.open(`${API}/orders/${orderId}/invoice`, '_blank');
  };

  return (
    <AdminLayout active="orders">
      <div className="flex items-center justify-between mb-4" data-testid="admin-orders">
        <h1 className="text-2xl font-bold text-slate-800">Objednávky</h1>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 border border-slate-300 rounded px-3 text-sm" data-testid="order-status-filter">
          <option value="">Všechny stavy</option>
          {Object.entries(STATUS_LABELS).map(([key, val]) => (<option key={key} value={key}>{val.label}</option>))}
        </select>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="orders-bulk-toolbar">
          <span className="text-sm font-medium text-blue-700">Vybráno: {selected.size}</span>
          <button onClick={bulkDeleteOrders} className="h-9 px-4 bg-red-600 text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-red-700" data-testid="bulk-delete-orders-btn">
            <Trash2 className="w-4 h-4" /> Smazat vybrané
          </button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-slate-500 hover:text-slate-700">Zrušit výběr</button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div> : orders.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Žádné objednávky</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-10"><input type="checkbox" checked={orders.length > 0 && selected.size === orders.length} onChange={() => selected.size === orders.length ? setSelected(new Set()) : setSelected(new Set(orders.map(o => o.id)))} className="rounded border-slate-300" /></th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Číslo</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Zákazník</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Datum</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase text-slate-500">Celkem</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Stav</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Zásilka</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Akce</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <OrderRow key={order.id} order={order} updateStatus={updateStatus} deleteOrder={deleteOrder} downloadInvoice={downloadInvoice}
                  expanded={expandedOrder === order.id} onToggleTracking={() => toggleTracking(order)}
                  trackingForm={trackingForm} setTrackingForm={setTrackingForm} savingTracking={savingTracking} onSaveTracking={() => saveTracking(order.id)}
                  selected={selected.has(order.id)} onToggleSelect={() => toggleSelect(order.id)}
                  detailOpen={detailOrder === order.id} onToggleDetail={() => setDetailOrder(detailOrder === order.id ? null : order.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

function OrderRow({ order, updateStatus, deleteOrder, downloadInvoice, expanded, onToggleTracking, trackingForm, setTrackingForm, savingTracking, onSaveTracking, selected, onToggleSelect, detailOpen, onToggleDetail }) {
  const addr = order.billing_address || {};
  const ship = order.shipping_address || addr;
  return (
    <>
      <tr className={`border-b border-slate-100 hover:bg-slate-50 ${selected ? 'bg-blue-50' : ''}`}>
        <td className="px-3 py-3"><input type="checkbox" checked={selected} onChange={onToggleSelect} className="rounded border-slate-300" /></td>
        <td className="px-4 py-3"><button onClick={onToggleDetail} className="font-medium text-[#004E98] hover:underline flex items-center gap-1" data-testid={`order-detail-${order.id}`}>{order.order_number} {detailOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</button></td>
        <td className="px-4 py-3"><div className="text-sm">{addr.firstname} {addr.lastname}</div><div className="text-xs text-slate-400">{addr.email}</div></td>
        <td className="px-4 py-3 text-sm text-slate-600">{new Date(order.created_at).toLocaleDateString('cs-CZ')}</td>
        <td className="px-4 py-3 text-right font-bold">{order.total?.toFixed(2)} Kč</td>
        <td className="px-4 py-3 text-center"><span className={`inline-block px-2 py-1 text-xs font-medium rounded ${STATUS_LABELS[order.status]?.color || 'bg-slate-100'}`}>{STATUS_LABELS[order.status]?.label || order.status}</span></td>
        <td className="px-4 py-3 text-center">
          {order.tracking_number ? (
            <div className="flex items-center justify-center gap-1">
              <Truck className="w-4 h-4 text-emerald-500" />
              {order.tracking_url ? <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#004E98] hover:underline">{order.tracking_number}</a> : <span className="text-xs text-slate-600">{order.tracking_number}</span>}
            </div>
          ) : <button onClick={onToggleTracking} className="text-xs text-slate-400 hover:text-[#004E98]"><Truck className="w-4 h-4" /></button>}
        </td>
        <td className="px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-1">
            <select value={order.status} onChange={(e) => updateStatus(order.id, e.target.value)} className="h-8 border border-slate-200 rounded text-xs px-2" data-testid={`order-status-${order.id}`}>
              {Object.entries(STATUS_LABELS).map(([key, val]) => (<option key={key} value={key}>{val.label}</option>))}
            </select>
            <button onClick={onToggleTracking} className="p-1.5 text-slate-400 hover:text-[#004E98] hover:bg-blue-50 rounded" title="Sledovací číslo"><Truck className="w-4 h-4" /></button>
            <button onClick={() => downloadInvoice(order.id, order.order_number)} className="p-1.5 text-slate-400 hover:text-[#004E98] hover:bg-blue-50 rounded" title="Stáhnout PDF"><FileDown className="w-4 h-4" /></button>
            <button onClick={() => deleteOrder(order.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Smazat" data-testid={`delete-order-${order.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </td>
      </tr>
      {/* Order detail */}
      {detailOpen && (
        <tr className="border-b border-slate-200 bg-slate-50/70">
          <td colSpan={8} className="px-4 py-4" data-testid={`order-detail-panel-${order.id}`}>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Fakturační adresa</h4>
                <p>{addr.firstname} {addr.lastname}</p>
                {addr.company && <p className="text-slate-500">{addr.company}</p>}
                <p>{addr.address1}</p>
                <p>{addr.postcode} {addr.city}</p>
                <p className="text-slate-500">{addr.email}</p>
                {addr.phone && <p className="text-slate-500">{addr.phone}</p>}
              </div>
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Doručovací adresa</h4>
                <p>{ship.firstname} {ship.lastname}</p>
                {ship.company && <p className="text-slate-500">{ship.company}</p>}
                <p>{ship.address1}</p>
                <p>{ship.postcode} {ship.city}</p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Platba</h4>
                <p>Způsob: <span className="font-medium">{order.payment_method === 'card' ? 'Kartou' : order.payment_method === 'cod' ? 'Dobírka' : order.payment_method}</span></p>
                <p>Stav: <span className={`font-medium ${order.payment_status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>{order.payment_status === 'paid' ? 'Zaplaceno' : 'Nezaplaceno'}</span></p>
                {order.note && <div className="mt-2"><h4 className="font-semibold text-slate-700">Poznámka</h4><p className="text-slate-500">{order.note}</p></div>}
              </div>
            </div>
            <div className="mt-4">
              <h4 className="font-semibold text-slate-700 mb-2">Položky objednávky</h4>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-500 border-b"><th className="text-left py-2">Produkt</th><th className="text-center py-2">Ks</th><th className="text-right py-2">Cena/ks</th><th className="text-right py-2">Celkem</th></tr></thead>
                <tbody>
                  {order.items?.map((item, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2">{item.name}</td>
                      <td className="py-2 text-center">{item.quantity}</td>
                      <td className="py-2 text-right">{(item.price_vat || item.price)?.toFixed(2)} Kč</td>
                      <td className="py-2 text-right font-medium">{item.total?.toFixed(2)} Kč</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={3} className="py-1 text-right text-slate-500">Mezisoučet:</td><td className="py-1 text-right">{order.subtotal?.toFixed(2)} Kč</td></tr>
                  {order.discount > 0 && <tr><td colSpan={3} className="py-1 text-right text-slate-500">Sleva ({order.coupon_code}):</td><td className="py-1 text-right text-red-500">-{order.discount?.toFixed(2)} Kč</td></tr>}
                  <tr><td colSpan={3} className="py-1 text-right text-slate-500">Doprava:</td><td className="py-1 text-right">{order.shipping_cost?.toFixed(2)} Kč</td></tr>
                  <tr className="font-bold"><td colSpan={3} className="py-2 text-right border-t">Celkem:</td><td className="py-2 text-right border-t text-[#004E98]">{order.total?.toFixed(2)} Kč</td></tr>
                </tfoot>
              </table>
            </div>
          </td>
        </tr>
      )}
      {/* Tracking form */}
      {expanded && (
        <tr className="border-b border-slate-200 bg-blue-50/50">
          <td colSpan={8} className="px-4 py-4">
            <div className="flex items-end gap-3 max-w-xl" data-testid={`tracking-form-${order.id}`}>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Přepravce</label>
                <select value={trackingForm.carrier} onChange={e => setTrackingForm(f => ({ ...f, carrier: e.target.value }))} className="h-10 border border-slate-300 rounded px-3 text-sm w-40" data-testid="tracking-carrier-select">
                  {CARRIER_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select></div>
              <div className="flex-1"><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Sledovací číslo</label>
                <input type="text" value={trackingForm.tracking_number} onChange={e => setTrackingForm(f => ({ ...f, tracking_number: e.target.value }))} placeholder="Zadejte sledovací číslo" className="w-full h-10 border border-slate-300 rounded px-4 text-sm" data-testid="tracking-number-input" /></div>
              <button onClick={onSaveTracking} disabled={savingTracking} className="h-10 px-4 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73] disabled:opacity-50" data-testid="save-tracking-btn">
                {savingTracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit</button>
              <button onClick={onToggleTracking} className="h-10 px-3 border border-slate-300 rounded text-sm hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ===================
// PRODUCTS
// ===================
const PER_PAGE_OPTIONS = [20, 50, 100];

export function AdminProducts() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showFilterDelete, setShowFilterDelete] = useState(false);
  const [filterForm, setFilterForm] = useState({ category_id: '', active: '', in_stock: '', search: '' });
  const [categories, setCategories] = useState([]);
  const [importing, setImporting] = useState(false);

  const loadProducts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: page.toString(), limit: perPage.toString() });
    if (search) params.set('search', search);
    axios.get(`${API}/products?${params.toString()}`)
      .then(res => { setProducts(res.data.products); setTotal(res.data.total); setSelected(new Set()); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search, perPage]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { axios.get(`${API}/categories`).then(res => setCategories(res.data)).catch(() => {}); }, []);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); };

  const deleteProduct = async (id) => {
    if (!window.confirm('Opravdu smazat produkt?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Produkt smazán');
      loadProducts();
    } catch { toast.error('Chyba mazání'); }
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => selected.size === products.length ? setSelected(new Set()) : setSelected(new Set(products.map(p => p.id_product)));

  const executeBulkAction = async () => {
    if (selected.size === 0) { toast.error('Vyberte produkty'); return; }
    const ids = Array.from(selected);
    setBulkProcessing(true);
    try {
      if (bulkAction === 'delete') {
        if (!window.confirm(`Opravdu smazat ${ids.length} produktů?`)) { setBulkProcessing(false); return; }
        const res = await axios.post(`${API}/admin/products/bulk-delete`, { ids }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Smazáno ${res.data.deleted_count} produktů`);
      } else if (bulkAction === 'activate') {
        const res = await axios.post(`${API}/admin/products/bulk-update`, { ids, active: 1 }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Aktivováno ${res.data.modified_count} produktů`);
      } else if (bulkAction === 'deactivate') {
        const res = await axios.post(`${API}/admin/products/bulk-update`, { ids, active: 0 }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Deaktivováno ${res.data.modified_count} produktů`);
      }
      loadProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba hromadné operace'); }
    finally { setBulkProcessing(false); setBulkAction(''); }
  };

  const executeFilterDelete = async () => {
    const f = {};
    if (filterForm.category_id) f.category_id = parseInt(filterForm.category_id);
    if (filterForm.active !== '') f.active = parseInt(filterForm.active);
    if (filterForm.in_stock !== '') f.in_stock = filterForm.in_stock === 'true';
    if (filterForm.search) f.search = filterForm.search;
    if (Object.keys(f).length === 0) { toast.error('Zadejte alespoň jeden filtr'); return; }
    if (!window.confirm('Opravdu smazat produkty dle filtru? Tato akce je nevratná!')) return;
    setBulkProcessing(true);
    try {
      const res = await axios.post(`${API}/admin/products/bulk-delete-filter`, f, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Smazáno ${res.data.deleted_count} produktů`);
      setShowFilterDelete(false);
      loadProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba mazání'); }
    finally { setBulkProcessing(false); }
  };

  const flatCats = (cats, depth = 0) => { let f = []; for (const c of cats) { f.push({ ...c, depth }); if (c.children?.length) f.push(...flatCats(c.children, depth + 1)); } return f; };
  const totalPages = Math.ceil(total / perPage);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/products/export-csv`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'products.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Chyba exportu'); }
  };
  const importCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/admin/products/import-csv`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      toast.success(`Import dokončen: ${res.data.created} vytvořeno, ${res.data.updated} aktualizováno`);
      if (res.data.errors?.length > 0) toast.error(`Chyby: ${res.data.errors.slice(0,3).join(', ')}`);
      loadProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba importu'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  return (
    <AdminLayout active="products">
      <div className="flex items-center justify-between mb-4" data-testid="admin-products">
        <h1 className="text-2xl font-bold text-slate-800">Produkty <span className="text-sm font-normal text-slate-500">({total.toLocaleString()})</span></h1>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="relative">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hledat..." className="h-10 w-56 border border-slate-300 rounded px-4 pr-10 text-sm" data-testid="product-search-input" />
            <button type="submit" className="absolute right-2 top-2"><Search className="w-5 h-5 text-slate-400" /></button>
          </form>
          <select value={perPage} onChange={e => { setPerPage(parseInt(e.target.value)); setPage(1); }} className="h-10 border border-slate-300 rounded px-2 text-sm" data-testid="per-page-select">
            {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n} na stránku</option>)}
          </select>
          <button onClick={() => setShowFilterDelete(!showFilterDelete)} className="h-10 px-3 border border-red-300 text-red-600 rounded text-sm hover:bg-red-50" data-testid="filter-delete-btn" title="Smazat dle filtru"><Trash2 className="w-4 h-4" /></button>
          <button onClick={exportCSV} className="h-10 px-3 border border-slate-300 text-slate-600 rounded text-sm hover:bg-slate-50 flex items-center gap-1" data-testid="export-products-csv"><Download className="w-4 h-4" /> CSV</button>
          <label className={`h-10 px-3 border border-slate-300 text-slate-600 rounded text-sm hover:bg-slate-50 flex items-center gap-1 cursor-pointer ${importing ? 'opacity-50' : ''}`} data-testid="import-products-csv">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import
            <input type="file" accept=".csv" onChange={importCSV} className="hidden" />
          </label>
          <button onClick={() => navigate('/admin/produkty/novy')} className="h-10 px-4 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73]" data-testid="add-product-btn"><Plus className="w-4 h-4" /> Nový</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="bulk-actions-toolbar">
          <span className="text-sm font-medium text-blue-700">Vybráno: {selected.size}</span>
          <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} className="h-9 border border-blue-300 rounded px-3 text-sm" data-testid="bulk-action-select">
            <option value="">Vyberte akci…</option>
            <option value="delete">Smazat vybrané</option>
            <option value="activate">Aktivovat</option>
            <option value="deactivate">Deaktivovat</option>
          </select>
          <button onClick={executeBulkAction} disabled={!bulkAction || bulkProcessing} className="h-9 px-4 bg-[#004E98] text-white rounded text-sm font-medium disabled:opacity-50 flex items-center gap-2" data-testid="execute-bulk-btn">
            {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Provést</button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-slate-500 hover:text-slate-700">Zrušit výběr</button>
        </div>
      )}

      {showFilterDelete && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="filter-delete-panel">
          <h3 className="font-semibold text-red-700 mb-3">Hromadné mazání dle filtru</h3>
          <div className="grid md:grid-cols-4 gap-3 mb-3">
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Kategorie</label>
              <select value={filterForm.category_id} onChange={e => setFilterForm(f => ({ ...f, category_id: e.target.value }))} className="w-full h-10 border border-slate-300 rounded px-3 text-sm" data-testid="filter-category"><option value="">Všechny</option>{flatCats(categories).map(c => <option key={c.id_category} value={c.id_category}>{'  '.repeat(c.depth)}{c.name}</option>)}</select></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Stav</label>
              <select value={filterForm.active} onChange={e => setFilterForm(f => ({ ...f, active: e.target.value }))} className="w-full h-10 border border-slate-300 rounded px-3 text-sm" data-testid="filter-active"><option value="">Všechny</option><option value="1">Aktivní</option><option value="0">Neaktivní</option></select></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Sklad</label>
              <select value={filterForm.in_stock} onChange={e => setFilterForm(f => ({ ...f, in_stock: e.target.value }))} className="w-full h-10 border border-slate-300 rounded px-3 text-sm" data-testid="filter-stock"><option value="">Všechny</option><option value="true">Skladem</option><option value="false">Vyprodáno</option></select></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Název</label>
              <input type="text" value={filterForm.search} onChange={e => setFilterForm(f => ({ ...f, search: e.target.value }))} className="w-full h-10 border border-slate-300 rounded px-4 text-sm" placeholder="Hledat..." data-testid="filter-search" /></div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={executeFilterDelete} disabled={bulkProcessing} className="h-9 px-4 bg-red-600 text-white rounded text-sm font-medium disabled:opacity-50 flex items-center gap-2 hover:bg-red-700" data-testid="execute-filter-delete-btn">{bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Smazat dle filtru</button>
            <button onClick={() => setShowFilterDelete(false)} className="text-sm text-slate-500 hover:text-slate-700">Zrušit</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div> : (
        <>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 w-10"><input type="checkbox" checked={products.length > 0 && selected.size === products.length} onChange={toggleSelectAll} className="rounded border-slate-300" data-testid="select-all-products" /></th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">ID</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Produkt</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Kód</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase text-slate-500">Cena</th>
                  <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Sklad</th>
                  <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Aktivní</th>
                  <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Akce</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id_product} className={`border-b border-slate-100 hover:bg-slate-50 ${selected.has(p.id_product) ? 'bg-blue-50' : ''}`}>
                    <td className="px-3 py-3"><input type="checkbox" checked={selected.has(p.id_product)} onChange={() => toggleSelect(p.id_product)} className="rounded border-slate-300" /></td>
                    <td className="px-4 py-3 text-sm text-slate-500">{p.id_product}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800 max-w-xs truncate">{p.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{p.reference || '-'}</td>
                    <td className="px-4 py-3 text-right font-medium">{p.price_vat?.toFixed(2)} Kč</td>
                    <td className="px-4 py-3 text-center"><span className={`text-xs px-2 py-0.5 rounded ${p.quantity > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{p.quantity > 0 ? p.quantity : 'Vyp.'}</span></td>
                    <td className="px-4 py-3 text-center">{p.active ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <X className="w-4 h-4 text-red-400 mx-auto" />}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => navigate(`/admin/produkty/${p.id_product}`)} className="p-1.5 text-[#004E98] hover:bg-blue-50 rounded" title="Upravit"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => deleteProduct(p.id_product)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Smazat"><Trash2 className="w-4 h-4" /></button>
                        <a href={`/produkt/${p.id_product}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:bg-slate-100 rounded" title="Náhled"><Eye className="w-4 h-4" /></a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500">Strana {page} z {totalPages || 1}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-2 border rounded text-sm disabled:opacity-50 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="px-3 py-2 border rounded text-sm disabled:opacity-50 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

// ===================
// PRODUCT EDIT / CREATE
// ===================
export function AdminProductEdit() {
  const { productId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isNew = productId === 'novy';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', description_short: '', price: 0, quantity: 0,
    reference: '', ean13: '', weight: 0, active: 1, on_sale: 0, id_category_default: null
  });

  useEffect(() => {
    if (!isNew) {
      axios.get(`${API}/products/${productId}`)
        .then(res => setForm(res.data))
        .catch(() => toast.error('Produkt nenalezen'))
        .finally(() => setLoading(false));
    }
  }, [productId, isNew]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API}/admin/products`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produkt vytvořen');
        navigate(`/admin/produkty/${res.data.id_product}`);
      } else {
        await axios.put(`${API}/admin/products/${productId}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produkt uložen');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba ukládání'); }
    finally { setSaving(false); }
  };

  const up = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) return <AdminLayout active="products"><div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div></AdminLayout>;

  return (
    <AdminLayout active="products">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/produkty')} className="text-slate-500 hover:text-[#004E98]"><ChevronLeft className="w-6 h-6" /></button>
        <h1 className="text-2xl font-bold text-slate-800" data-testid="product-edit-title">{isNew ? 'Nový produkt' : `Upravit: ${form.name}`}</h1>
      </div>
      <form onSubmit={handleSave} className="max-w-4xl space-y-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-semibold text-slate-800 mb-4">Základní informace</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Název *</label>
              <input type="text" required value={form.name} onChange={e => up('name', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="product-name-input" /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Cena bez DPH (Kč)</label>
              <input type="number" step="0.01" value={form.price} onChange={e => up('price', parseFloat(e.target.value) || 0)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="product-price-input" /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Cena s DPH (Kč)</label>
              <input type="number" step="0.01" value={form.price_vat || Math.round(form.price * 1.21 * 100) / 100} onChange={e => up('price_vat', parseFloat(e.target.value) || 0)} className="w-full h-11 border border-slate-300 rounded px-4" /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Skladem (ks)</label>
              <input type="number" value={form.quantity} onChange={e => up('quantity', parseInt(e.target.value) || 0)} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="product-quantity-input" /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Kód (reference)</label>
              <input type="text" value={form.reference || ''} onChange={e => up('reference', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">EAN</label>
              <input type="text" value={form.ean13 || ''} onChange={e => up('ean13', e.target.value)} className="w-full h-11 border border-slate-300 rounded px-4" /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Hmotnost (kg)</label>
              <input type="number" step="0.01" value={form.weight || 0} onChange={e => up('weight', parseFloat(e.target.value) || 0)} className="w-full h-11 border border-slate-300 rounded px-4" /></div>
            <div className="flex items-center gap-6 pt-6">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.active === 1} onChange={e => up('active', e.target.checked ? 1 : 0)} className="w-4 h-4 text-[#004E98] rounded" /><span className="text-sm font-medium">Aktivní</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.on_sale === 1} onChange={e => up('on_sale', e.target.checked ? 1 : 0)} className="w-4 h-4 text-[#FF6B35] rounded" /><span className="text-sm font-medium">Akce</span></label>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-semibold text-slate-800 mb-4">Popis</h2>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Krátký popis</label>
              <textarea value={form.description_short || ''} onChange={e => up('description_short', e.target.value)} rows={3} className="w-full border border-slate-300 rounded p-3 text-sm" /></div>
            <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Plný popis (HTML)</label>
              <textarea value={form.description || ''} onChange={e => up('description', e.target.value)} rows={8} className="w-full border border-slate-300 rounded p-3 text-sm font-mono" /></div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} className="h-11 px-6 bg-[#004E98] text-white rounded font-medium flex items-center gap-2 hover:bg-[#003B73] disabled:opacity-50" data-testid="save-product-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isNew ? 'Vytvořit produkt' : 'Uložit změny'}</button>
          <button type="button" onClick={() => navigate('/admin/produkty')} className="h-11 px-6 border border-slate-300 rounded text-sm hover:bg-slate-50">Zrušit</button>
        </div>
      </form>
    </AdminLayout>
  );
}

// ===================
// CMS PAGES
// ===================
export function AdminPages() {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '' });

  useEffect(() => { axios.get(`${API}/pages`).then(res => setPages(res.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  const startEdit = (page) => { setEditing(page.slug); setEditForm({ title: page.title, content: page.content }); };
  const saveEdit = async (slug) => {
    try {
      await axios.put(`${API}/admin/pages/${slug}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      setPages(prev => prev.map(p => p.slug === slug ? { ...p, ...editForm } : p));
      toast.success('Stránka uložena'); setEditing(null);
    } catch { toast.error('Chyba ukládání'); }
  };

  return (
    <AdminLayout active="pages">
      <h1 className="text-2xl font-bold text-slate-800 mb-6" data-testid="admin-pages">CMS Stránky</h1>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div> : (
        <div className="space-y-4">
          {pages.map(page => (
            <div key={page.slug} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
                <div><span className="font-semibold text-slate-800">{page.title}</span><span className="text-xs text-slate-400 ml-2">/{page.slug}</span></div>
                <div className="flex items-center gap-2">
                  <a href={`/${page.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><Eye className="w-4 h-4" /></a>
                  {editing === page.slug ? (
                    <><button onClick={() => saveEdit(page.slug)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"><Save className="w-4 h-4" /></button><button onClick={() => setEditing(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button></>
                  ) : (<button onClick={() => startEdit(page)} className="p-1.5 text-[#004E98] hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>)}
                </div>
              </div>
              {editing === page.slug && (
                <div className="p-5 space-y-4">
                  <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Název stránky</label><input type="text" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="w-full h-11 border border-slate-300 rounded px-4" /></div>
                  <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Obsah (HTML)</label><textarea value={editForm.content} onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))} rows={15} className="w-full border border-slate-300 rounded p-3 text-sm font-mono" /></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

// ===================
// SETTINGS (re-export)
// ===================
export { default as AdminSettings } from "@/components/AdminSettings";
